/**
 * App Context
 *
 * Manages the currently selected app for the user.
 * Provides the app ID and name to all child components.
 *
 * Features:
 * - Auto-selects if user has only one app
 * - Provides selector if user has multiple apps
 * - Falls back to "no app" state with prompt to create one
 */

import {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";

interface AppContextValue {
  appId: string | null;
  appName: string | null;
  isLoading: boolean;
  apps: Array<{ id: string; name: string }>;
  selectApp: (appId: string) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

  // TODO: Add apps router with list procedure
  const apps: Array<{ id: string; name: string }> = [];
  const isLoading = false;

  // Auto-select if only one app
  useEffect(() => {
    if (apps.length === 1 && !selectedAppId) {
      setSelectedAppId(apps[0]!.id);
    }
  }, [apps, selectedAppId]);

  const selectedApp = apps.find((app) => app.id === selectedAppId);

  const contextValue: AppContextValue = {
    appId: selectedAppId,
    appName: selectedApp?.name ?? null,
    isLoading,
    apps,
    selectApp: setSelectedAppId,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

/**
 * Hook to access the current app context
 *
 * @returns Current app ID, name, loading state, and selector
 * @throws If used outside of AppProvider
 */
export function useCurrentApp(): AppContextValue {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error("useCurrentApp must be used within an AppProvider");
  }

  return context;
}
